import React, { useCallback, useEffect, useMemo } from 'react';

import debounce from 'lodash/debounce';

import AssigneeFilter from './assignee';
import DateRangeFilter from './date_range';
import DateTypeFilter from './date_type';
import DealUserFilter from './deal_user';
import OfficeFilter from './office';
import ParentTypeFilter from './parent_type';
import QueryFilter from './query';
import StatusFilter from './status';
import { getDefaults, isFilterEnabled, serializeFilters } from './helpers';
import { useQueryParamsState } from 'hooks/use_query_param_state';

import type { ApiFilters, Filters, FilterProps } from './types';

import 'components/styles/search_page.scss';

const config = {
  assigneeId: 'number',
  completed: 'boolean',
  dealUserId: 'number',
  fromDate: 'string',
  officeId: 'number',
  open: 'boolean',
  overdue: 'boolean',
  parentType: 'string',
  query: 'string',
  tbd: 'boolean',
  toDate: 'string',
} as const;

export interface Props {
  assignee?: FilterProps;
  children?: React.ReactNode;
  dateRange?: FilterProps;
  dateType?: FilterProps;
  dealUser?: FilterProps;
  oneLine?: boolean;
  options: {
    offices: {
      label: string;
      value: number;
    }[];
    users: {
      id: number;
      name: string;
    }[];
  };
  parent?: FilterProps;
  status?: FilterProps;
  onChange: (filters: ApiFilters) => void;
  onChangePending: () => void;
}

export default function TasksIndexFilters({ children, oneLine, options, onChange, onChangePending, ...props }: Props) {
  const users = useMemo(() => options.users.filter(option => option.id), []);
  const [filters, setFilters] = useQueryParamsState<Filters>(
    config,
    getDefaults({
      dateRangeEnabled: isFilterEnabled(props, 'dateRange'),
      tbdEnabled: isFilterEnabled(props, 'dateType'),
    }),
    { prefix: 'tasks' }
  );

  // Initialize parent component once from initial query param state
  useEffect(() => {
    onChange(serializeFilters(filters));
  }, []);

  const debouncedOnChange = useCallback(debounce(onChange, 500), [onChange]);
  function changeFilters(changes: Partial<Filters>, debounce = false) {
    const changedFilters = { ...filters, ...changes } as Filters;
    setFilters(changedFilters);
    onChangePending();
    const callback = debounce ? debouncedOnChange : onChange;
    callback(serializeFilters(changedFilters));
  }

  const filterProps = { ...filters, onChange: changeFilters };

  const topLine = <>
    <QueryFilter {...filterProps} onChange={changes => changeFilters(changes, true)} />
    {isFilterEnabled(props, 'dateRange') && <DateRangeFilter {...filterProps} />}
  </>;

  const bottomLine = <>
    {isFilterEnabled(props, 'status') && <StatusFilter {...filterProps} />}
    {isFilterEnabled(props, 'parent') && <ParentTypeFilter {...filterProps} />}
    {isFilterEnabled(props, 'dateType') && <DateTypeFilter {...filterProps} />}
    {isFilterEnabled(props, 'office') && <OfficeFilter {...filterProps} offices={options.offices} />}
    {isFilterEnabled(props, 'dealUser') && <DealUserFilter {...filterProps} users={users} />}
    {isFilterEnabled(props, 'assignee') && <AssigneeFilter {...filterProps} users={users} />}
  </>;

  let content;
  if (oneLine) {
    content = <>
      <div className="d-flex justify-content-between">
        <div className="d-flex flex-wrap align-items-center">
          {topLine}
          {bottomLine}
        </div>

        {children}
      </div>
    </>;
  } else {
    content = <>
      <div className="d-flex justify-content-between">
        {topLine}
        {children}
      </div>

      <div className="d-flex flex-wrap">
        {bottomLine}
      </div>
    </>;
  }

  return (
    <div className="search-page">
      <div className="w-100">
        {content}
      </div>
    </div>
  );
}
